export type MissionType = 'daily' | 'weekly' | 'monthly' | 'lifetime';
export type MissionCategory = 'gameplay' | 'social' | 'performance' | 'streak';

export interface MissionReward {
  type: 'coins' | 'exp' | 'tickets' | 'badge' | 'vfx' | 'gems' | 'prism' | 'box' | 'black_prism' | 'star_ticket';
  amount: number | string;
  stars?: number;
  subType?: string;
}

export interface MissionDefinition {
  id: string;
  name: string;
  objective: string;
  type: MissionType;
  category: MissionCategory;
  goal: number;
  rewards: MissionReward[];
  isPremiumOnly?: boolean;
}

export const MISSIONS: MissionDefinition[] = [
  // ==========================================
  // DAILY MISSIONS (4 + 1 Perfection)
  // ==========================================
  {
    id: 'warm_up',
    name: 'Warm Up',
    objective: 'Play 3 games of any mode.',
    type: 'daily',
    category: 'gameplay',
    goal: 3,
    rewards: [{ type: 'tickets', amount: 1 }]
  },
  {
    id: 'brain_burner',
    name: 'Brain Burner',
    objective: 'Trigger Fever Time 5 times.',
    type: 'daily',
    category: 'gameplay',
    goal: 5,
    rewards: [{ type: 'coins', amount: 1000 }]
  },
  {
    id: 'high_scorer_daily',
    name: 'High Scorer',
    objective: 'Reach a combined score of 30,000 across all games.',
    type: 'daily',
    category: 'performance',
    goal: 30000,
    rewards: [{ type: 'coins', amount: 1000 }]
  },
  {
    id: 'window_shopper',
    name: 'Window Shopper',
    objective: 'Open 1 Reward Box (Bronze or Ad box).',
    type: 'daily',
    category: 'gameplay',
    goal: 1,
    rewards: [{ type: 'prism', amount: 1, stars: 1 }]
  },
  {
    id: 'pro_fever_frenzy',
    name: 'Fever Frenzy',
    objective: 'Trigger Fever Time 6 times.',
    type: 'daily',
    category: 'performance',
    goal: 6,
    rewards: [{ type: 'gems', amount: 15 }],
    isPremiumOnly: true
  },
  {
    id: 'pro_box_scavenger',
    name: 'Box Scavenger',
    objective: 'Open 2 Reward Boxes of any tier.',
    type: 'daily',
    category: 'gameplay',
    goal: 2,
    rewards: [{ type: 'prism', amount: 1, stars: 2 }],
    isPremiumOnly: true
  },
  {
    id: 'daily_perfection',
    name: 'Daily Perfection',
    objective: 'Complete all 4 Daily Missions.',
    type: 'daily',
    category: 'streak',
    goal: 4,
    rewards: [
      { type: 'gems', amount: 10 }
    ]
  },

  // ==========================================
  // WEEKLY MISSIONS (9 + 2 Milestones)
  // ==========================================
  {
    id: 'dedicated',
    name: 'Dedicated',
    objective: 'Log in 5 days this week.',
    type: 'weekly',
    category: 'streak',
    goal: 5,
    rewards: [{ type: 'gems', amount: 10 }]
  },
  {
    id: 'the_grinder',
    name: 'The Grinder',
    objective: 'Play 30 games total.',
    type: 'weekly',
    category: 'gameplay',
    goal: 30,
    rewards: [{ type: 'coins', amount: 5000 }]
  },
  {
    id: 'cloudmach_expert',
    name: 'CLOUD MACH Expert',
    objective: 'Play 5 games of CLOUD MACH.',
    type: 'weekly',
    category: 'gameplay',
    goal: 5,
    rewards: [{ type: 'prism', amount: 1, stars: 2 }]
  },
  {
    id: 'shellcrush_expert',
    name: 'Shell Crush Expert',
    objective: 'Play 5 games of Shell Crush.',
    type: 'weekly',
    category: 'gameplay',
    goal: 5,
    rewards: [{ type: 'prism', amount: 1, stars: 2 }]
  },
  {
    id: 'goldrush_expert',
    name: 'Gold Rush Expert',
    objective: 'Play 5 games of Gold Rush.',
    type: 'weekly',
    category: 'gameplay',
    goal: 5,
    rewards: [{ type: 'prism', amount: 1, stars: 2 }]
  },
  {
    id: 'unboxer_weekly',
    name: 'Unboxer',
    objective: 'Open 5 Reward Boxes (any tier).',
    type: 'weekly',
    category: 'gameplay',
    goal: 5,
    rewards: [{ type: 'coins', amount: 5000 }]
  },
  {
    id: 'the_alchemist',
    name: 'The Alchemist',
    objective: 'Obtain a 2-Star Prism (Merge or Box).',
    type: 'weekly',
    category: 'performance',
    goal: 1,
    rewards: [{ type: 'box', amount: 1, subType: 'silver' }]
  },
  {
    id: 'big_spender_weekly',
    name: 'Big Spender',
    objective: 'Spend 20,000 Coins in the shop.',
    type: 'weekly',
    category: 'gameplay',
    goal: 20000,
    rewards: [{ type: 'gems', amount: 25 }]
  },
  {
    id: 'routine_maker',
    name: 'Routine Maker',
    objective: 'Complete "Daily Perfection" 3 times this week.',
    type: 'weekly',
    category: 'streak',
    goal: 3,
    rewards: [{ type: 'box', amount: 1, subType: 'gold' }]
  },
  {
    id: 'weekly_checkup',
    name: '6 7',
    objective: 'Complete 6 weekly Missions.',
    type: 'weekly',
    category: 'streak',
    goal: 6,
    rewards: [
      { type: 'box', amount: 1, subType: 'silver' },
      { type: 'gems', amount: 50 }
    ]
  },
  {
    id: 'pro_grand_tour',
    name: 'Grand Tour',
    objective: 'Play 5 games of Cloud Mach, Shell Crush, AND Gold Rush.',
    type: 'weekly',
    category: 'gameplay',
    goal: 15,
    rewards: [{ type: 'box', amount: 1, subType: 'gold' }],
    isPremiumOnly: true
  },
  {
    id: 'pro_prism_refiner',
    name: 'Prism Refiner',
    objective: 'Perform a Reroll or Merge on any Prism.',
    type: 'weekly',
    category: 'performance',
    goal: 1,
    rewards: [{ type: 'gems', amount: 50 }],
    isPremiumOnly: true
  },
  {
    id: 'weekly_perfection',
    name: 'Weekly Perfection',
    objective: 'Complete all 9 Weekly Missions.',
    type: 'weekly',
    category: 'streak',
    goal: 9,
    rewards: [
      { type: 'box', amount: 1, subType: 'gold' },
      { type: 'gems', amount: 100 }
    ]
  },

  // ==========================================
  // MONTHLY MISSIONS (17 + 2 Milestones)
  // ==========================================
  {
    id: 'numq_veteran',
    name: 'numQ Veteran',
    objective: 'Log in 20 days this month.',
    type: 'monthly',
    category: 'streak',
    goal: 20,
    rewards: [{ type: 'star_ticket', amount: 1 }]
  },
  {
    id: 'marathon_monthly',
    name: 'Marathon',
    objective: 'Play 150 games total.',
    type: 'monthly',
    category: 'gameplay',
    goal: 150,
    rewards: [{ type: 'coins', amount: 10000 }]
  },
  {
    id: 'cloudmach_expert_2',
    name: 'CLOUD MACH Expert II',
    objective: 'Play 25 games of CLOUD MACH.',
    type: 'monthly',
    category: 'gameplay',
    goal: 25,
    rewards: [{ type: 'prism', amount: 1, stars: 2 }]
  },
  {
    id: 'shellcrush_expert_2',
    name: 'Shell Crush Expert II',
    objective: 'Play 25 games of Shell Crush.',
    type: 'monthly',
    category: 'gameplay',
    goal: 25,
    rewards: [{ type: 'prism', amount: 1, stars: 2 }]
  },
  {
    id: 'goldrush_expert_2',
    name: 'Gold Rush Expert II',
    objective: 'Play 25 games of Gold Rush.',
    type: 'monthly',
    category: 'gameplay',
    goal: 25,
    rewards: [{ type: 'prism', amount: 1, stars: 2 }]
  },
  {
    id: 'cloudmach_expert_3',
    name: 'CLOUD MACH Expert III',
    objective: 'Play 50 games of CLOUD MACH.',
    type: 'monthly',
    category: 'gameplay',
    goal: 50,
    rewards: [{ type: 'prism', amount: 1, stars: 3 }]
  },
  {
    id: 'shellcrush_expert_3',
    name: 'Shell Crush Expert III',
    objective: 'Play 50 games of Shell Crush.',
    type: 'monthly',
    category: 'gameplay',
    goal: 50,
    rewards: [{ type: 'prism', amount: 1, stars: 3 }]
  },
  {
    id: 'goldrush_expert_3',
    name: 'Gold Rush Expert III',
    objective: 'Play 50 games of Gold Rush.',
    type: 'monthly',
    category: 'gameplay',
    goal: 50,
    rewards: [{ type: 'prism', amount: 1, stars: 3 }]
  },
  {
    id: 'starburst_challenger',
    name: 'Star Burst Challenger',
    objective: 'Play 1 game of Star Burst.',
    type: 'monthly',
    category: 'gameplay',
    goal: 1,
    rewards: [{ type: 'box', amount: 1, subType: 'gold' }]
  },
  {
    id: 'craftsman',
    name: 'Craftsman',
    objective: 'Perform a "Reroll" on any Prism.',
    type: 'monthly',
    category: 'performance',
    goal: 1,
    rewards: [{ type: 'coins', amount: 10000 }]
  },
  {
    id: 'master_alchemist',
    name: 'Master Alchemist',
    objective: 'Obtain a 3-Star Prism.',
    type: 'monthly',
    category: 'performance',
    goal: 1,
    rewards: [{ type: 'prism', amount: 1, stars: 3 }]
  },
  {
    id: 'whale_watcher',
    name: 'Whale Watcher',
    objective: 'Spend 50,000 Coins in the shop.',
    type: 'monthly',
    category: 'gameplay',
    goal: 50000,
    rewards: [{ type: 'box', amount: 1, subType: 'gold' }]
  },
  {
    id: 'bronze_of_wisdom',
    name: 'Bronze of Wisdom',
    objective: 'Open 50 Bronze Boxes.',
    type: 'monthly',
    category: 'gameplay',
    goal: 50,
    rewards: [{ type: 'box', amount: 1, subType: 'diamond' }]
  },
  {
    id: 'risk_taker',
    name: 'The Risk Taker',
    objective: 'Perform 5 Rerolls on any Prism.',
    type: 'monthly',
    category: 'performance',
    goal: 5,
    rewards: [{ type: 'prism', amount: 1, stars: 3 }]
  },
  {
    id: 'adrenaline_junkie',
    name: 'Adrenaline Junkie',
    objective: 'Trigger Fever Time 100 times this month.',
    type: 'monthly',
    category: 'performance',
    goal: 100,
    rewards: [{ type: 'coins', amount: 15000 }]
  },
  {
    id: 'box_hoarder',
    name: 'Box Hoarder',
    objective: 'Open 3 Gold tier boxes.',
    type: 'monthly',
    category: 'gameplay',
    goal: 3,
    rewards: [{ type: 'coins', amount: 10000 }]
  },
  {
    id: 'box_crusher',
    name: 'Box Crusher',
    objective: 'Open 3 Diamond tier boxes.',
    type: 'monthly',
    category: 'gameplay',
    goal: 3,
    rewards: [{ type: 'coins', amount: 15000 }]
  },
  {
    id: 'monthly_checkup',
    name: 'Monthly Checkup',
    objective: 'Complete 14 Monthly Missions.',
    type: 'monthly',
    category: 'streak',
    goal: 14,
    rewards: [
      { type: 'box', amount: 1, subType: 'diamond' },
      { type: 'gems', amount: 100 }
    ]
  },
  {
    id: 'pro_diamond_hoarder',
    name: 'Diamond Hoarder',
    objective: 'Open 2 Diamond tier boxes.',
    type: 'monthly',
    category: 'gameplay',
    goal: 2,
    rewards: [{ type: 'prism', amount: 1, stars: 3 }],
    isPremiumOnly: true
  },
  {
    id: 'pro_fever_marathon',
    name: 'Overdrive Sovereign',
    objective: 'Trigger Fever Time 150 times this month.',
    type: 'monthly',
    category: 'performance',
    goal: 150,
    rewards: [
      { type: 'box', amount: 1, subType: 'diamond' },
      { type: 'gems', amount: 150 }
    ],
    isPremiumOnly: true
  },
  {
    id: 'monthly_perfection',
    name: 'Monthly Perfection',
    objective: 'Complete 17 Monthly Missions.',
    type: 'monthly',
    category: 'streak',
    goal: 17,
    rewards: [
      { type: 'box', amount: 3, subType: 'diamond' },
      { type: 'star_ticket', amount: 1 }
    ]
  }
];

// ==========================================
// LIFETIME MISSIONS (15 Categories x 4 Tiers)
// ==========================================
// Generated based on vanguardSystem definitions but as individual claimable missions

export const LIFETIME_CATEGORIES: {
  name: string, 
  cat: MissionCategory, 
  goalField: string, 
  unit: string, 
  tiers: number[],
  names: string[],
  objectives: string[],
  rewards: MissionReward[][]
}[] = [
  { 
    name: 'User Level', 
    cat: 'streak', 
    goalField: 'level', 
    unit: 'Level', 
    tiers: [25, 50, 75, 100],
    names: ['Rising Star', 'Veteran Solver', 'Master Mind', 'Grand Legend'],
    objectives: ['Reach Level 25', 'Reach Level 50', 'Reach Level 75', 'Reach Level 100'],
    rewards: [
      [{ type: 'coins', amount: 10000 }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'coins', amount: 50000 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }, { type: 'star_ticket', amount: 1 }]
    ]
  },
  { 
    name: 'Anima Summoner', 
    cat: 'gameplay', 
    goalField: 'counters.animaTicketsOpened', 
    unit: 'Tickets', 
    tiers: [10, 50, 100, 250],
    names: ['Novice Summoner', 'Adept Summoner', 'Expert Summoner', 'Master Summoner'],
    objectives: ['Open 10 Anima Tickets', 'Open 50 Anima Tickets', 'Open 100 Anima Tickets', 'Open 250 Anima Tickets'],
    rewards: [
      [{ type: 'gems', amount: 50 }],
      [{ type: 'gems', amount: 150 }],
      [{ type: 'gems', amount: 300 }],
      [{ type: 'box', amount: 1, subType: 'diamond' }, { type: 'star_ticket', amount: 1 }]
    ]
  },
  { 
    name: 'Star Burst (HS)', 
    cat: 'performance', 
    goalField: 'highScores.starburst', 
    unit: 'Pts', 
    tiers: [5, 10, 15, 20],
    names: ['Quick Reflexes', 'Supernova Streak', 'Galactic Rhythm', 'Stellar Perfection'],
    objectives: ['Reach 5 Pts in Star Burst', 'Reach 10 Pts in Star Burst', 'Reach 15 Pts in Star Burst', 'Reach 20 Pts in Star Burst'],
    rewards: [
      [{ type: 'coins', amount: 5000 }],
      [{ type: 'box', amount: 1, subType: 'gold' }],
      [{ type: 'prism', amount: 1, stars: 3 }, { type: 'star_ticket', amount: 1 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }, { type: 'star_ticket', amount: 3 }]
    ]
  },
  { 
    name: 'Cloud Mach (HS)', 
    cat: 'performance', 
    goalField: 'highScores.pemdas', 
    unit: 'Pts', 
    tiers: [3000, 6000, 10000, 15000],
    names: ['Sky High', 'Stratus Surfer', 'Nimbus Navigator', 'Heavenly High Score'],
    objectives: ['Reach 3,000 Pts in Cloud Mach', 'Reach 6,000 Pts in Cloud Mach', 'Reach 10,000 Pts in Cloud Mach', 'Reach 15,000 Pts in Cloud Mach'],
    rewards: [
      [{ type: 'coins', amount: 5000 }],
      [{ type: 'box', amount: 1, subType: 'gold' }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }]
    ]
  },
  { 
    name: 'Gold Rush (HS)', 
    cat: 'performance', 
    goalField: 'highScores.goldrush', 
    unit: 'Pts', 
    tiers: [3000, 6000, 10000, 15000],
    names: ['Gold Prospector', 'Midas Touch', 'Treasury Raider', 'Fortune Finder'],
    objectives: ['Reach 3,000 Pts in Gold Rush', 'Reach 6,000 Pts in Gold Rush', 'Reach 10,000 Pts in Gold Rush', 'Reach 15,000 Pts in Gold Rush'],
    rewards: [
      [{ type: 'coins', amount: 5000 }],
      [{ type: 'box', amount: 1, subType: 'gold' }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }]
    ]
  },
  { 
    name: 'Shell Crush (HS)', 
    cat: 'performance', 
    goalField: 'highScores.shellcrush', 
    unit: 'Pts', 
    tiers: [3000, 6000, 10000, 15000],
    names: ['Cracked Surface', 'Tidal Impact', 'Oceanic Overpower', 'Abyssal Breaker'],
    objectives: ['Reach 3,000 Pts in Shell Crush', 'Reach 6,000 Pts in Shell Crush', 'Reach 10,000 Pts in Shell Crush', 'Reach 15,000 Pts in Shell Crush'],
    rewards: [
      [{ type: 'coins', amount: 5000 }],
      [{ type: 'box', amount: 1, subType: 'gold' }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }]
    ]
  },
  { 
    name: 'Investment (Coins)', 
    cat: 'gameplay', 
    goalField: 'counters.total_gc_spent', 
    unit: 'Coins', 
    tiers: [100000, 1000000, 2500000, 10000000],
    names: ['Big Spender', 'Gold Magnate', 'High Roller', 'Economic Titan'],
    objectives: ['Spend 100,000 Coins', 'Spend 1,000,000 Coins', 'Spend 2,500,000 Coins', 'Spend 10,000,000 Coins'],
    rewards: [
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'box', amount: 1, subType: 'diamond' }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }, { type: 'gems', amount: 250 }]
    ]
  },
  { 
    name: 'Demolition', 
    cat: 'gameplay', 
    goalField: 'counters.blocks_destroyed', 
    unit: 'Blocks', 
    tiers: [5000, 20000, 50000, 100000],
    names: ['Brick Breaker', 'Wall Buster', 'Architect of Ruin', 'Total Obliteration'],
    objectives: ['Destroy 5,000 Blocks', 'Destroy 20,000 Blocks', 'Destroy 50,000 Blocks', 'Destroy 100,000 Blocks'],
    rewards: [
      [{ type: 'coins', amount: 5000 }],
      [{ type: 'box', amount: 1, subType: 'gold' }],
      [{ type: 'coins', amount: 15000 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }, { type: 'prism', amount: 1, stars: 3 }]
    ]
  },
  { 
    name: 'Persistence', 
    cat: 'gameplay', 
    goalField: 'counters.hearts_consumed', 
    unit: 'Hearts', 
    tiers: [25, 100, 500, 1000],
    names: ['Second Wind', 'Unstoppable Will', 'Undying Spirit', 'Eternal Vitality'],
    objectives: ['Consume 25 Hearts', 'Consume 100 Hearts', 'Consume 500 Hearts', 'Consume 1,000 Hearts'],
    rewards: [
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'coins', amount: 10000 }],
      [{ type: 'box', amount: 2, subType: 'diamond' }],
      [{ type: 'box', amount: 5, subType: 'diamond' }, { type: 'star_ticket', amount: 1 }]
    ]
  },
  { 
    name: 'Adrenaline', 
    cat: 'gameplay', 
    goalField: 'counters.fever_count', 
    unit: 'Fevers', 
    tiers: [20, 100, 500, 1000],
    names: ['Sparked Up', 'Heart Racer', 'Overdrive Master', 'Hyper-Speed Soul'],
    objectives: ['Trigger 20 Fevers', 'Trigger 100 Fevers', 'Trigger 500 Fevers', 'Trigger 1,000 Fevers'],
    rewards: [
      [{ type: 'box', amount: 1, subType: 'gold' }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'box', amount: 1, subType: 'diamond' }],
      [{ type: 'box', amount: 3, subType: 'diamond' }]
    ]
  },
  { 
    name: 'Consistency', 
    cat: 'streak', 
    goalField: 'dailyGift.currentStreak', 
    unit: 'Days', 
    tiers: [14, 50, 100, 300],
    names: ['Fortnight Hero', 'Monthly Routine', 'Century Bond', 'Yearly Commitment'],
    objectives: ['14 Days Total Login', '50 Days Total Login', '100 Days Total Login', '300 Days Total Login'],
    rewards: [
      [{ type: 'gems', amount: 50 }],
      [{ type: 'gems', amount: 150 }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }, { type: 'gems', amount: 300 }]
    ]
  },
  { 
    name: 'Hoshi Reserve', 
    cat: 'gameplay', 
    goalField: 'counters.total_cc_spent', 
    unit: 'Hoshi', 
    tiers: [1000, 10000, 50000, 100000],
    names: ['Bright Spark', 'Shining Asset', 'Cosmic Investor', 'Star-Lord Status'],
    objectives: ['Spend 1,000 Hoshi', 'Spend 10,000 Hoshi', 'Spend 50,000 Hoshi', 'Spend 100,000 Hoshi'],
    rewards: [
      [{ type: 'box', amount: 1, subType: 'gold' }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'coins', amount: 50000 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }, { type: 'star_ticket', amount: 1 }]
    ]
  },
  { 
    name: 'Star Marathon', 
    cat: 'gameplay', 
    goalField: 'counters.plays_starburst', 
    unit: 'Plays', 
    tiers: [10, 50, 100, 250],
    names: ['Star Cadet', 'Star Pilot', 'Star Commander', 'Star Emperor'],
    objectives: ['Play Star Burst 10 Times', 'Play Star Burst 50 Times', 'Play Star Burst 100 Times', 'Play Star Burst 250 Times'],
    rewards: [
      [{ type: 'gems', amount: 50 }],
      [{ type: 'gems', amount: 250 }],
      [{ type: 'gems', amount: 500 }],
      [{ type: 'gems', amount: 1000 }]
    ]
  },
  { 
    name: 'Cloud Marathon', 
    cat: 'gameplay', 
    goalField: 'counters.plays_cloud', 
    unit: 'Plays', 
    tiers: [25, 100, 500, 1000],
    names: ['Cloud Drifter', 'Cloud Soarer', 'Storm Chaser', 'King of the Clouds'],
    objectives: ['Play Cloud Mach 25 Times', 'Play Cloud Mach 100 Times', 'Play Cloud Mach 500 Times', 'Play Cloud Mach 1,000 Times'],
    rewards: [
      [{ type: 'coins', amount: 20000 }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'coins', amount: 100000 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }]
    ]
  },
  { 
    name: 'Gold Marathon', 
    cat: 'gameplay', 
    goalField: 'counters.plays_gold', 
    unit: 'Plays', 
    tiers: [25, 100, 500, 1000],
    names: ['Penny Pincher', 'Bar Collector', 'Vault Guardian', 'The Golden Legend'],
    objectives: ['Play Gold Rush 25 Times', 'Play Gold Rush 100 Times', 'Play Gold Rush 500 Times', 'Play Gold Rush 1,000 Times'],
    rewards: [
      [{ type: 'coins', amount: 20000 }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'coins', amount: 100000 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }]
    ]
  },
  { 
    name: 'Shell Marathon', 
    cat: 'gameplay', 
    goalField: 'counters.plays_shell', 
    unit: 'Plays', 
    tiers: [25, 100, 500, 1000],
    names: ['Shell Scavenger', 'Pearl Hunter', 'Deep Sea Diver', 'Lord of the Tides'],
    objectives: ['Play Shell Crush 25 Times', 'Play Shell Crush 100 Times', 'Play Shell Crush 500 Times', 'Play Shell Crush 1,000 Times'],
    rewards: [
      [{ type: 'coins', amount: 20000 }],
      [{ type: 'prism', amount: 1, stars: 3 }],
      [{ type: 'coins', amount: 100000 }],
      [{ type: 'box', amount: 5, subType: 'diamond' }]
    ]
  },
];

LIFETIME_CATEGORIES.forEach((vc, catIdx) => {
  vc.tiers.forEach((threshold, tierIdx) => {
    MISSIONS.push({
      id: `${catIdx + 1}.${tierIdx + 1}`,
      name: vc.names[tierIdx],
      objective: vc.objectives[tierIdx],
      type: 'lifetime',
      category: vc.cat,
      goal: threshold,
      rewards: vc.rewards[tierIdx]
    });
  });
});

// Academy/Tutorial Phase & Graduation Lifetime Missions
MISSIONS.push(
  {
    id: 'tutorial_shellcrush',
    name: 'Shell Crush Graduate',
    objective: 'Complete Shell Crush training.',
    type: 'lifetime',
    category: 'gameplay',
    goal: 1,
    rewards: [
      { type: 'coins', amount: 5000 },
      { type: 'gems', amount: 100 }
    ]
  },
  {
    id: 'tutorial_goldrush',
    name: 'Gold Rush Graduate',
    objective: 'Complete Gold Rush training.',
    type: 'lifetime',
    category: 'gameplay',
    goal: 1,
    rewards: [
      { type: 'coins', amount: 5000 },
      { type: 'gems', amount: 100 }
    ]
  },
  {
    id: 'tutorial_pemdas',
    name: 'Cloud Mach Graduate',
    objective: 'Complete Cloud Mach training.',
    type: 'lifetime',
    category: 'gameplay',
    goal: 1,
    rewards: [
      { type: 'coins', amount: 5000 },
      { type: 'gems', amount: 100 }
    ]
  },
  {
    id: 'tutorial_starburst',
    name: 'Star Burst Graduate',
    objective: 'Complete Star Burst training.',
    type: 'lifetime',
    category: 'gameplay',
    goal: 1,
    rewards: [
      { type: 'coins', amount: 5000 },
      { type: 'gems', amount: 100 }
    ]
  },
  {
    id: 'tutorial_graduation',
    name: 'Academy Graduate',
    objective: 'Complete the entire Academy tutorial.',
    type: 'lifetime',
    category: 'streak',
    goal: 1,
    rewards: [
      { type: 'coins', amount: 50000 },
      { type: 'gems', amount: 500 },
      { type: 'exp', amount: 3000 },
      { type: 'prism', amount: 5, stars: 1 }
    ]
  },
  {
    id: 'pro_lifetime_solver',
    name: 'Pro: Golden Solver',
    objective: 'Reach level 60.',
    type: 'lifetime',
    category: 'streak',
    goal: 60,
    rewards: [{ type: 'box', amount: 1, subType: 'diamond' }, { type: 'star_ticket', amount: 1 }],
    isPremiumOnly: true
  }
);



